import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Grid,
  InputAdornment,
  Select,
  Box,
  useTheme,
  Typography,
  FormControl,
  Autocomplete,
} from "@mui/material";

import { useDispatch } from "react-redux";
import {
  criteriaOptions,
  findStrategy,
  findStrategyById,
  hashGetBlacklistCritera,
  hashGetBlacklistType,
  operationOptions,
} from "../../component/Methods";
import { toast } from "react-toastify";
import { sectorOptions, assetOptions } from "../../component/Methods/common";
import {
  addActiveRequests, 
  updateLoading, 
  updateLoadingMsg 
} from "../../Redux/Reducers/uiSlice";
import AddIcon from "@mui/icons-material/Add";

import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { getFundType } from "../../component/Methods/mainFunctions";
import { useFundsBlacklistState } from "../../hooks/useOptimizedState";

const FundsBlacklistDialog = ({
  open,
  editedRule,
  onClose,
  onSave,
  selectedFund,
  title,
  blacklistData,
}) => {
  const { strategies, managers } = useFundsBlacklistState();
  const dispatch = useDispatch();
  const BlacklistData = blacklistData;
  const [strategy, setStrategy] = useState("All");
  const [tickers, setTickers] = useState([]);
  const [isRuleAdded, setIsRuleAdded] = useState(false);
  const [myrule, setMyrule] = useState({
    criteria: "",
    operation: "",
    value: "",
    unit: "",
  });
  const updatedStrategies = strategies ? [...strategies] : [];
  if (!updatedStrategies.some((strategy) => strategy.name === "All")) {
    updatedStrategies.unshift({ id: "all", name: "All" });
  }
  useEffect(() => {
    if (editedRule) {
      try {
        const {
          type,
          maximum,
          minimum,
          ticker,
          strategy,
          sectors,
          assetTypes,
        } = editedRule;
        let operation = null;
        let value = null;

        if (minimum !== undefined && maximum !== undefined) {
          operation = "=";
          value = minimum;
        } else if (maximum !== undefined) {
          operation = ">";
          value = maximum;
        } else if (minimum !== undefined) {
          operation = "<";
          value = minimum;
        }
        setStrategy(
          editedRule?.strategy
            ? findStrategy(editedRule.strategy, strategies)
            : "All"
        );
        setTickers(editedRule?.ticker ? [editedRule.ticker] : ["All"]);
        // Convert sectors and assetTypes to arrays if they're strings
        let sectorsArray = [];
        let assetTypesArray = [];

        if (type == 5 && sectors) {
          if (Array.isArray(sectors)) {
            sectorsArray = sectors;
          } else if (typeof sectors === "string") {
            sectorsArray = sectors
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s !== "");
          }
        }

        if (type == 6 && assetTypes) {
          if (Array.isArray(assetTypes)) {
            assetTypesArray = assetTypes;
          } else if (typeof assetTypes === "string") {
            assetTypesArray = assetTypes
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s !== "");
          }
        }

        setMyrule({
          criteria: hashGetBlacklistCritera(type),
          operation: type == 5 || type == 6 ? "=" : operation,
          value: type == 5 ? sectorsArray : type == 6 ? assetTypesArray : value,
          unit: editedRule.unit || "",
        });
        setIsRuleAdded(true);
      } catch (error) {
        console.error("Error processing editedRule:", error);
        // Reset to default values if there's an error
        setStrategy("All");
        setTickers(["All"]);
        setMyrule({
          criteria: "",
          operation: "",
          value: "",
          unit: "",
        });
        setIsRuleAdded(false);
      }
    } else {
      setStrategy("All");
      setTickers(["All"]);
      setMyrule({
        criteria: "",
        operation: "",
        value: "",
        unit: "",
      });
      setIsRuleAdded(false);
    }
  }, [editedRule, strategies]);

  const handleStrategyChange = (event) => {
    setStrategy(event.target.value);
  };

  const handleTickerChange = (event) => {
    const newTickers = event.target.value
      .split(",")
      .map((ticker) => ticker.trim());
    setTickers(newTickers);
  };

  const handleRuleChange = (field, value) => {
    if (field === "criteria") {
      if (value === "Sectors" || value === "Assets") {
        setMyrule((prevMyrule) => ({
          ...prevMyrule,
          operation: "=",
          value: [],
          [field]: value,
        }));
      } else {
        setMyrule((prevMyrule) => ({
          ...prevMyrule,
          [field]: value,
          operation: "",
          value: "",
        }));
      }
    } else {
      setMyrule((prevMyrule) => ({
        ...prevMyrule,
        [field]: value,
      }));
    }
  };

  const handleSave = () => {
    const { criteria, operation, value, unit } = myrule;
    const ruleObj = {
      type: hashGetBlacklistType(criteria),
    };
    const normalizedValue = Number(value);

    if (ruleObj.type === 5) {
      ruleObj.sectors = value;
    } else if (ruleObj.type === 6) {
      ruleObj.assetTypes = value;
    } else {
      if (operation === "<") {
        ruleObj.minimum = normalizedValue;
      } else if (operation === ">") {
        ruleObj.maximum = normalizedValue;
      } else if (operation === "=") {
        ruleObj.maximum = normalizedValue;
        ruleObj.minimum = normalizedValue;
      }
      ruleObj.unit = unit;
    }

    const updatedRule = tickers.includes("All")
      ? [
          {
            ...(strategy !== "All" && {
              strategy: findStrategyById(strategy, strategies),
            }),
            ...ruleObj,
          },
        ]
      : tickers.map((ticker) => ({
          ...(strategy !== "All" && {
            strategy: findStrategyById(strategy, strategies),
          }),
          ticker: ticker,
          ...ruleObj,
        }));
    const isDuplicate = BlacklistData?.rules?.some((existingRule) =>
      updatedRule.some((newRule) => {
        if (existingRule.type !== newRule.type) return false;

        const existingStrategyId = existingRule.strategy?.strategyId || "All";
        const newStrategyId = newRule.strategy || "All";
        if (existingStrategyId !== newStrategyId) return false;

        const existingTickers = existingRule.tickers || [];
        const newTicker = newRule.ticker || "All";

        if (newTicker === "All") return true;

        return existingTickers.some(
          (ticker) => ticker.toLowerCase() === newTicker.toLowerCase()
        );
      })
    );

    if (isDuplicate && !editedRule) {
      toast.error("This rule already exists!");
      return;
    }

    const isModification =
      BlacklistData?.rules && BlacklistData.rules?.length > 0 ? true : false;

    const data = {
      action: isModification ? "modify_blacklist" : "create_blacklist_rule",
      data: {
        rules: isModification
          ? {
              add: [],
              modify: [],
              remove: [],
            }
          : [],
      },
    };

    if (isModification && editedRule) {
      // For editing: remove the old rule and add the new one
      let newTickers = [];
      if (updatedRule.ticker && updatedRule.ticker !== "All") {
        newTickers = updatedRule.ticker
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t !== "");
      }
      data.data.rules.add.push(...updatedRule);
      newTickers.forEach((ticker) => {
        data.data.rules.add.push({
          ...(editedRule.strategy && { strategy: editedRule.strategy }),
          ticker: ticker,
          ...oldRuleObj,
        });
      });

      // Remove the old rule - handle multiple tickers properly
      const oldRuleObj = {
        type: editedRule.type,
      };

      // Get old tickers from the consolidated rule (comma-separated string)
      let oldTickers = [];
      if (editedRule.ticker && editedRule.ticker !== "All") {
        // Split comma-separated tickers
        oldTickers = editedRule.ticker
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t !== "");
      }

      if (oldTickers.length === 0) {
        // No specific tickers - remove the general rule
        data.data.rules.remove.push({
          ...(editedRule.strategy && { strategy: editedRule.strategy }),
          ...oldRuleObj,
        });
      } else {
        // Remove individual rules for each ticker
        oldTickers.forEach((ticker) => {
          data.data.rules.remove.push({
            ...(editedRule.strategy && { strategy: editedRule.strategy }),
            ticker: ticker,
            ...oldRuleObj,
          });
        });
      }

      const fundType = getFundType(selectedFund, managers);
      if (fundType.blacklistType === "sma") {
        data.data.smaId = fundType.sma;
      } else if (fundType.blacklistType === "fund") {
        data.data.fundId = fundType.fund;
      } else if (fundType.blacklistType === "manager") {
        data.data.managerId = fundType.manager;
      }
    } else if (isModification && !editedRule) {
      data.data.rules.add.push(...updatedRule);
      const fundType = getFundType(selectedFund, managers);
      if (fundType.blacklistType === "sma") {
        data.data.smaId = fundType.sma;
      } else if (fundType.blacklistType === "fund") {
        data.data.fundId = fundType.fund;
      } else if (fundType.blacklistType === "manager") {
        data.data.managerId = fundType.manager;
      }
    } else {
      data.data.rules.push(...updatedRule);
      const fundType = getFundType(selectedFund, managers);
      data.data.fund = fundType.fund;
      data.data.sma = fundType.sma;
      data.data.manager = fundType.manager;
      data.data.blacklistType = fundType.blacklistType;
    }
    sendWebSocketMessage(JSON.stringify(data));
    // setBlacklistOpenPopup(false);
    if (isModification) {
      dispatch(addActiveRequests("modify_blacklist"));
    } else {
      dispatch(addActiveRequests("create_blacklist_rule"));
    }

    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating Blacklist"));

    onClose();
  };

  const theme = useTheme();

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      maxWidth="xs"
      sx={{
        "& .css-1t1j96h-MuiPaper-root-MuiDialog-paper": {
          minWidth: "1000px",
          height: "100vh",
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              color: "white",
            }}
          >
            <AddIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
            >
              Configure blacklist rules for the selected fund
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ fontSize: "0.875rem" }}>
          Select Strategy
        </Typography>
        <FormControl fullWidth size="small" variant="outlined">
          <Select
            value={strategy}
            onChange={handleStrategyChange}
            name="strategy"
            sx={{ height: "50px" }}
          >
            {updatedStrategies?.map((strategyItem) => (
              <MenuItem key={strategyItem.id} value={strategyItem.name}>
                {strategyItem.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography
          variant="subtitle1"
          sx={{ marginTop: 2, fontSize: "0.875rem" }}
        >
          Enter Tickers (comma separated)
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          value={tickers}
          onChange={handleTickerChange}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Typography
          variant="subtitle1"
          sx={{ marginTop: 2, fontSize: "0.875rem" }}
        >
          Criteria
        </Typography>

        <Grid container spacing={1} alignItems="center">
          <Grid size={{ xs: 5 }}>
            <Select
              fullWidth
              size="small"
              value={myrule?.criteria || ""}
              onChange={(e) => handleRuleChange("criteria", e.target.value)}
              displayEmpty
            >
              {criteriaOptions.map((option, index) => (
                <MenuItem key={`${option.value}-${index}`} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid size={{ xs: 2 }}>
            <Select
              fullWidth
              size="small"
              value={myrule?.operation || ""}
              onChange={(e) => handleRuleChange("operation", e.target.value)}
              disabled={
                !myrule?.criteria ||
                myrule?.criteria === "NotAllowed" ||
                myrule?.criteria === "Sectors" ||
                myrule?.criteria === "Assets"
              }
            >
              {operationOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {myrule.criteria === "Sectors" ? (
            <Grid size={{ xs: 5 }}>
              <Autocomplete
                multiple
                size="small"
                id="sectors-autocomplete"
                options={sectorOptions}
                value={Array.isArray(myrule.value) ? myrule.value : []}
                fullWidth
                onChange={(event, newValue) =>
                  handleRuleChange("value", newValue)
                }
                disabled={!myrule?.criteria}
                renderInput={(params) => (
                  <TextField {...params} label="Select Sectors" />
                )}
              />
            </Grid>
          ) : myrule.criteria === "Assets" ? (
            <Grid size={{ xs: 5 }}>
              <Autocomplete
                multiple
                size="small"
                id="assets-autocomplete"
                options={assetOptions}
                value={Array.isArray(myrule.value) ? myrule.value : []}
                fullWidth
                onChange={(event, newValue) =>
                  handleRuleChange("value", newValue)
                }
                disabled={!myrule?.criteria}
                renderInput={(params) => (
                  <TextField {...params} label="Select Assets" />
                )}
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 5 }}>
              <TextField
                fullWidth
                size="small"
                value={myrule.value}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleRuleChange("value", value);
                  }
                }}
                disabled={
                  !myrule?.criteria || myrule?.criteria === "NotAllowed"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {myrule.unit}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          sx={{
            marginTop: "20px",
            float: "right",
            marginRight: "10px",
            marginBottom: "20px",
            padding: "8px 30px",
          }}
          onClick={() => {
            handleSave();
          }}
          disabled={
            !myrule.criteria ||
            (myrule.criteria !== "NotAllowed" &&
              (!myrule.operation ||
                (myrule.criteria !== "Sectors" &&
                  myrule.criteria !== "Assets" &&
                  !myrule.value))) ||
            ((myrule.criteria === "Sectors" || myrule.criteria === "Assets") &&
              Array.isArray(myrule.value) &&
              myrule.value.length === 0)
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FundsBlacklistDialog;
